import {graphql} from 'gatsby'
import React from 'react'
import styled from 'styled-components'

import Button from '@/components/button'
import FeatureBlock from '@/components/feature-block'
import Footer from '@/components/footer'
import Header from '@/components/header'
import Image from '@/components/image'
import LocalizedLink from '@/components/localized-link'
import MediaList from '@/components/media-list'
import SEO from '@/components/seo'
import {Main} from '@/components/skip-to-content'
import SmoothScrollLink from '@/components/smooth-scroll-link'
import {Text, TextLink} from '@/components/text'
import SignupBannerSection from '@/sections/signup-banner'
import TitleWithAccordion from '@/sections/title-with-accordion'
import {Section, BackgroundWrapper, Layout} from '@/styles/grid'
import {colors, tokens, fonts} from '@/theme'
import getEnvironmentFormId from '@/utils/get-environment-field'
import {getVideoSources} from '@/utils/get-video-sources'
import handleNoPrismicData from '@/utils/handle-no-prismic-data'
import mq from '@/utils/media-query'
import {withPrismicPreview} from '@/utils/with-prismic-preview'

import type {ApplePayQuery} from '@/graphql-types'
import type {FormConfig} from '@/types/consents'
import type {GatsbyHeadProps} from '@/types/gatsby-head'
import type {PageProps} from 'gatsby'

type Props = PageProps<ApplePayQuery>

const ApplePay = (props: Props) => {
    const data = props.data.allPrismicApplePayPage.nodes[0].data

    if (!data) {
        return handleNoPrismicData()
    }

    const fastPaymentsVideo = getVideoSources({
        webm: data.fast_payments[0].video_webm,
        mp4: data.fast_payments[0].video_mp4,
    })

    const applePayVideo = getVideoSources({
        webm: data.check_out[0].video_webm,
        mp4: data.check_out[0].video_mp4,
    })

    return (
        <>
            <Header />

            <Main>
                <Section padding="small" hideOverflow>
                    <FeatureBlock
                        image={data.hero_image}
                        title={data.title}
                        body={data.subtitle.richText}
                        titleVariant="heading-large"
                    >
                        {data.hero_badge && <HeroBadge image={data.hero_badge} />}
                    </FeatureBlock>
                </Section>

                <Section>
                    <FeatureBlock
                        image={data.fast_payments[0].image}
                        inlineVideo={fastPaymentsVideo}
                        title={data.fast_payments[0].title}
                        body={data.fast_payments[0].body.richText}
                        mirrored
                    />
                </Section>

                <Section>
                    <FeatureBlock
                        image={data.safe_payments[0].image}
                        title={data.safe_payments[0].title}
                        body={data.safe_payments[0].body.richText}
                    />
                </Section>

                <Section>
                    <FeatureBlock
                        image={data.check_out[0].image}
                        inlineVideo={applePayVideo}
                        title={data.check_out[0].title}
                        body={data.check_out[0].body.richText}
                        mirrored
                    />
                </Section>

                <BackgroundWrapper $color="purple">
                    <Layout as={Section} $variant="two-asymmetrical">
                        <AddCardHeader>
                            <Text $variant="heading-large">{data.add_card_title}</Text>
                            <Text>
                                {data.add_card_subtitle}{' '}
                                <TextLink
                                    $bgColor="black"
                                    as={SmoothScrollLink}
                                    selector="#signupform"
                                >
                                    {data.add_card_cta_text}
                                </TextLink>
                            </Text>
                        </AddCardHeader>

                        <MediaList
                            items={data.add_card_steps.map((item) => ({
                                ...item,
                                body: item.body.richText,
                            }))}
                        />
                    </Layout>
                </BackgroundWrapper>

                <TitleWithAccordion
                    title={data.questions_title}
                    items={data.questions_answers?.map((item) => ({
                        title: item.question,
                        body: item.answer?.richText,
                    }))}
                />

                {data.signup_banner && (
                    <SignupBannerSection
                        {...data.signup_banner.document.data}
                        color="pink"
                        formConfig={
                            getEnvironmentFormId({
                                production: data.signup_banner.document.data.form_production,
                                staging: data.signup_banner.document.data.form_staging,
                            }) as FormConfig
                        }
                        placeholder={data.signup_banner.document.data.email_input_placeholder}
                        submitText={data.signup_banner.document.data.submit_text}
                        errorText={data.signup_banner.document.data.email_input_error_text}
                    />
                )}

                <BenefitsTitleSection>
                    <Text as="h3" $variant="heading-large">
                        {data.benefits_title}
                    </Text>
                </BenefitsTitleSection>

                {data.page_type === 'google' && (
                    <PerksSection>
                        <FeatureBlock
                            image={data.perks[0].image}
                            title={data.perks[0].title}
                            body={data.perks[0].body.richText}
                            mirrored
                        >
                            <Button as={LocalizedLink} to="/pocket" $variant="outline">
                                {data.perks[0].cta_text}
                            </Button>
                        </FeatureBlock>
                    </PerksSection>
                )}

                <FetchSection>
                    <FeatureBlock
                        image={data.fetch[0].image}
                        title={data.fetch[0].title}
                        body={data.fetch[0].body.richText}
                    >
                        <Button as={LocalizedLink} to="/fetch" $variant="outline">
                            {data.fetch[0].cta_text}
                        </Button>
                    </FeatureBlock>
                </FetchSection>
            </Main>

            <Footer />
        </>
    )
}

export const Head = ({location, pageContext, data}: GatsbyHeadProps<ApplePayQuery>) => {
    const pageData = data.allPrismicApplePayPage.nodes[0].data

    return (
        <SEO
            location={location}
            pageContext={pageContext}
            title={pageData.meta_title}
            description={pageData.meta_description}
            openGraphImageUrl={pageData.open_graph_image?.url}
        />
    )
}

const HeroBadge = styled(Image)`
    ${mq.max('sm')} {
        margin: 0 auto;
    }
`

const PerksSection = styled(Section)`
    strong {
        font-weight: ${fonts.weights.normal};
        background: ${colors.background.purple};
    }
`

const FetchSection = styled(Section)`
    strong {
        font-weight: ${fonts.weights.normal};
        background: ${colors.background.yellow};
    }
`

const BenefitsTitleSection = styled(Section)`
    > * {
        max-width: 620px;
    }
`

const AddCardHeader = styled.div`
    display: grid;
    align-content: start;
    gap: ${tokens.spacing20};
`

export const query = graphql`
    query ApplePay($uid: String!, $locale: String!) {
        allPrismicApplePayPage(filter: {uid: {eq: $uid}, lang: {eq: $locale}}) {
            nodes {
                _previewable
                data {
                    meta_title
                    meta_description
                    open_graph_image {
                        alt
                        copyright
                        url
                        dimensions {
                            height
                            width
                        }
                    }
                    page_type
                    hero_badge {
                        alt
                        copyright
                        url
                        dimensions {
                            height
                            width
                        }
                    }
                    title
                    subtitle {
                        richText
                    }
                    hero_image {
                        alt
                        copyright
                        url
                        dimensions {
                            height
                            width
                        }
                    }
                    fast_payments {
                        title
                        body {
                            richText
                        }
                        image {
                            alt
                            copyright
                            url
                            dimensions {
                                height
                                width
                            }
                        }
                        video_mp4 {
                            ...Video
                        }
                        video_webm {
                            ...Video
                        }
                    }
                    safe_payments {
                        title
                        body {
                            richText
                        }
                        image {
                            alt
                            copyright
                            url
                            dimensions {
                                height
                                width
                            }
                        }
                    }
                    check_out {
                        video_mp4 {
                            ...Video
                        }
                        video_webm {
                            ...Video
                        }
                        title
                        body {
                            richText
                        }
                        image {
                            alt
                            copyright
                            url
                            dimensions {
                                height
                                width
                            }
                        }
                    }
                    add_card_title
                    add_card_subtitle
                    add_card_cta_text
                    add_card_steps {
                        title
                        body {
                            richText
                        }
                        image {
                            alt
                            copyright
                            url
                            dimensions {
                                height
                                width
                            }
                        }
                    }
                    questions_title
                    questions_answers {
                        question
                        answer {
                            richText
                        }
                    }
                    benefits_title
                    perks {
                        title
                        body {
                            richText
                        }
                        image {
                            alt
                            copyright
                            url
                            dimensions {
                                height
                                width
                            }
                        }
                        cta_text
                    }
                    fetch {
                        title
                        body {
                            richText
                        }
                        image {
                            alt
                            copyright
                            url
                            dimensions {
                                height
                                width
                            }
                        }
                        cta_text
                    }
                    signup_banner {
                        document {
                            ...SignupBanner
                        }
                    }
                }
            }
        }
    }
`

export default withPrismicPreview(ApplePay)
